/**
 * File upload rules — the SINGLE source of truth for every upload in the
 * product: what may be uploaded, how large it may be, and what its object path
 * in Storage looks like.
 *
 * Why this file owns the path too. The import flow used to build its Storage
 * path out of the USER'S filename (sanitised on the client) while the
 * `file-import` Edge Function validated that path against a stricter whitelist
 * of its own. The two rules disagreed on the very first character they were
 * ever asked about — a space — so `dcp_projections_import_template 082526.xlsx`
 * uploaded fine and was then rejected with "Invalid file path", leaving its
 * `file_imports` row stuck at `pending` forever. Two copies of one rule is the
 * bug; deleting one copy is the fix.
 *
 * The path is now generated, never derived from user input, so there is nothing
 * to sanitise and nothing to disagree about: a user may name their file
 * anything at all — spaces, Cyrillic, emoji — and it still lands as
 * `<organizationId>/<prefix>-<uuid>.<ext>`. The original name is preserved on
 * `file_imports.file_name` and is what the UI displays.
 *
 * Imported by the web app AND by Deno Edge Functions, so it stays dependency
 * free and uses only APIs both runtimes have (`crypto.randomUUID`).
 */

/** Extensions accepted by the CSV/XLSX import flow. */
export const IMPORT_ALLOWED_EXTENSIONS = ['csv', 'xlsx'] as const

/** Extensions accepted for an organization logo. */
export const LOGO_ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'svg'] as const

export type ImportFileExtension = (typeof IMPORT_ALLOWED_EXTENSIONS)[number]
export type LogoFileExtension = (typeof LOGO_ALLOWED_EXTENSIONS)[number]

/**
 * File upload size and type constraints.
 *
 * Client-side validation gives the user a fast, friendly error; the SERVER must
 * enforce the same limits, because a client check is a courtesy, not a control.
 */
export const FILE_UPLOAD_LIMITS = {
  /**
   * Transaction / projection / balance imports.
   */
  IMPORT: {
    /** Maximum file size in bytes (50MB) */
    MAX_SIZE_BYTES: 50 * 1024 * 1024,
    /** Maximum file size label for display */
    MAX_SIZE_LABEL: '50 MB',
    /** Allowed file extensions */
    ALLOWED_EXTENSIONS: IMPORT_ALLOWED_EXTENSIONS,
    /** `accept` attribute for the file input */
    ACCEPT_ATTRIBUTE: '.csv,.xlsx',
    /** Storage bucket these objects live in */
    BUCKET: 'import-files',
    /** Object-name prefix, so a path is recognisable at a glance in the bucket */
    PATH_PREFIX: 'import',
  },

  /**
   * Organization logo upload constraints
   */
  ORGANIZATION_LOGO: {
    /** Maximum file size in bytes (5MB) */
    MAX_SIZE_BYTES: 5 * 1024 * 1024,
    /** Maximum file size label for display */
    MAX_SIZE_LABEL: '5 MB',
    /**
     * Allowed MIME types for validation.
     *
     * Must stay a SUBSET of the bucket's `allowed_mime_types` (see the
     * `organization-logos` bucket in `00000000000000_schema.sql`) — anything
     * accepted here but rejected there passes the friendly client-side check
     * only to fail at Storage with a raw error. `image/jpg` is not a real MIME
     * type and is not in the bucket list, so it stays out; JPEGs report
     * `image/jpeg`.
     */
    ALLOWED_TYPES: ['image/png', 'image/jpeg', 'image/svg+xml'] as const,
    /** Allowed file extensions */
    ALLOWED_EXTENSIONS: LOGO_ALLOWED_EXTENSIONS,
    /** Storage bucket these objects live in */
    BUCKET: 'organization-logos',
    /** Object-name prefix */
    PATH_PREFIX: 'logo',
  },
} as const

/**
 * Characters a generated Storage object path may contain.
 *
 * Every path this module produces satisfies it by construction; the Edge
 * Function checks incoming paths against the same expression so a hand-crafted
 * request cannot smuggle a traversal segment past it.
 */
const SAFE_PATH_CHARACTERS = /^[a-zA-Z0-9/_.-]+$/

/**
 * Extract a file's extension, lower-cased, with no dot.
 *
 * Returns null when the name has no usable extension. Note this reads the
 * user's filename purely to CLASSIFY the file — the value never reaches the
 * Storage path.
 *
 * @param fileName - Original filename as supplied by the user
 */
export function fileExtensionOf(fileName: string): string | null {
  const parts = fileName.split('.')
  if (parts.length < 2) return null
  const extension = parts[parts.length - 1].toLowerCase().trim()
  return extension.length > 0 ? extension : null
}

/**
 * Build the Storage object path for an upload.
 *
 * The user's filename is deliberately NOT part of it. The path is
 * `<organizationId>/<prefix>-<uuid>.<extension>`, which is safe by
 * construction, free of collisions, and stable in length no matter what the
 * file was called.
 *
 * @param organizationId - Owning organization; also the folder, which is what
 *   the storage policies and the Edge Function's prefix check key on
 * @param extension - Already-validated extension, without a dot
 * @param prefix - Short label identifying the flow (e.g. 'import', 'logo')
 * @returns A path guaranteed to satisfy {@link isSafeStoragePath}
 */
export function buildStorageObjectPath(
  organizationId: string,
  extension: string,
  prefix: string,
): string {
  return `${organizationId}/${prefix}-${crypto.randomUUID()}.${extension.toLowerCase()}`
}

/**
 * Whether a Storage object path is safe to hand to the storage layer.
 *
 * Rejects anything that could escape the organization's folder or confuse a
 * downstream consumer: a path outside the org prefix, `..` segments, doubled or
 * trailing slashes, dot-segments (hidden files), or characters outside
 * {@link SAFE_PATH_CHARACTERS}.
 *
 * Shared with the Edge Function on purpose: when the generator and the
 * validator are the same rule, "uploaded fine, then rejected" is not a state
 * the system can reach.
 *
 * @param filePath - Path to check
 * @param organizationId - Organization the path must be scoped to
 */
export function isSafeStoragePath(filePath: string, organizationId: string): boolean {
  if (!filePath.startsWith(`${organizationId}/`)) return false
  if (filePath.includes('..')) return false
  if (filePath.includes('//')) return false
  if (filePath.endsWith('/')) return false
  if (filePath.split('/').some((segment) => segment.startsWith('.'))) return false
  return SAFE_PATH_CHARACTERS.test(filePath)
}

/** Why an upload was rejected; `null` extension means "no extension at all". */
export interface UploadRejection {
  reason: 'extension' | 'size'
  /** Message suitable for showing to the user as-is. */
  message: string
}

/**
 * Validate a file against one of the {@link FILE_UPLOAD_LIMITS} entries.
 *
 * Returns the rejection rather than throwing, so the caller decides how to
 * surface it (a toast on the client, an HTTP status on the server). `null`
 * means the file is acceptable.
 *
 * @param file - Size and name of the file being uploaded
 * @param rules - The limits entry for this flow
 */
export function validateUploadFile(
  file: { name: string; size: number },
  rules: {
    MAX_SIZE_BYTES: number
    MAX_SIZE_LABEL: string
    ALLOWED_EXTENSIONS: readonly string[]
  },
): UploadRejection | null {
  const extension = fileExtensionOf(file.name)
  if (!extension || !rules.ALLOWED_EXTENSIONS.includes(extension)) {
    return {
      reason: 'extension',
      message: `Unsupported file type. Please use ${rules.ALLOWED_EXTENSIONS.map((e) => e.toUpperCase()).join(' or ')}.`,
    }
  }

  if (file.size > rules.MAX_SIZE_BYTES) {
    return {
      reason: 'size',
      message: `File is too large. Maximum size is ${rules.MAX_SIZE_LABEL}.`,
    }
  }

  return null
}
